import { useEffect, useState } from "react";
import {
  Alert,
  Box,
  Button,
  FormControlLabel,
  InputAdornment,
  Paper,
  Radio,
  RadioGroup,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import {
  notifyError,
  notifySuccess,
} from "../../../components/ToastContainer/Toast/Toast";
import {
  getTopupSettings,
  saveTopupSettings,
  TopupSettings,
} from "../../../service/BuyerConfiguration/Wallets/walletService";

/**
 * Add money (top-up) settings.
 *
 * Who pays Razorpay's fee, the fee %, and every limit can be changed here at
 * any time; the Buyer API applies them to the next top-up immediately. Each
 * top-up keeps the fee rule it was made under.
 */
const TopupSettingsCard = () => {
  const [form, setForm] = useState<Record<keyof TopupSettings, any> | null>(null);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    getTopupSettings()
      .then((s) => setForm({ ...s }))
      .catch((e: any) => notifyError(e?.response?.data?.message || "Failed to load top-up settings"));
  }, []);

  if (!form) return null;

  const num = (v: any) => Number(v);
  const problem = (() => {
    if (!(num(form.min) > 0)) return "Minimum must be more than ₹0.";
    if (num(form.max) < num(form.min)) return "Maximum must be at least the minimum.";
    if (num(form.balance_cap) < num(form.max)) return "Balance cap must be at least the maximum top-up.";
    if (num(form.daily_cap) < num(form.min)) return "Daily limit must be at least the minimum.";
    if (!(num(form.fee_percent) >= 0 && num(form.fee_percent) <= 10)) return "Fee percent must be between 0 and 10.";
    return null;
  })();

  const example = 500;
  const exampleFee =
    form.fee_bearer === "customer" ? Math.ceil((example * num(form.fee_percent)) / 100) : 0;

  const save = async () => {
    if (problem) return;
    setSaving(true);
    try {
      const saved = await saveTopupSettings({
        enabled: !!form.enabled,
        min: num(form.min),
        max: num(form.max),
        balance_cap: num(form.balance_cap),
        daily_cap: num(form.daily_cap),
        fee_bearer: form.fee_bearer,
        fee_percent: num(form.fee_percent),
      });
      setForm({ ...saved });
      notifySuccess("Top-up settings saved");
    } catch (e: any) {
      notifyError(e?.response?.data?.message || "Could not save top-up settings");
    } finally {
      setSaving(false);
    }
  };

  const money = (key: keyof TopupSettings, label: string) => (
    <TextField
      size="small"
      label={label}
      value={form[key]}
      onChange={(e) => setForm({ ...form, [key]: e.target.value.replace(/[^\d.]/g, "") })}
      InputProps={{ startAdornment: <InputAdornment position="start">₹</InputAdornment> }}
      sx={{ minWidth: 170 }}
    />
  );

  return (
    <Paper variant="outlined" sx={{ p: 2, mb: 2 }}>
      <Stack direction="row" alignItems="center" justifyContent="space-between" sx={{ mb: 1 }}>
        <Box>
          <Typography variant="subtitle1" sx={{ fontWeight: 600 }}>
            Add money (top-up) settings
          </Typography>
          <Typography variant="caption" sx={{ opacity: 0.7 }}>
            Changes apply to the next top-up immediately. Past top-ups keep their fee.
          </Typography>
        </Box>
        <FormControlLabel
          control={
            <Switch checked={!!form.enabled} onChange={(e) => setForm({ ...form, enabled: e.target.checked })} />
          }
          label={form.enabled ? "Add money ON" : "Add money OFF"}
        />
      </Stack>

      <Stack direction={{ xs: "column", md: "row" }} spacing={3} sx={{ mb: 2 }}>
        <Box>
          <Typography variant="body2" sx={{ fontWeight: 500 }}>
            Razorpay fee paid by
          </Typography>
          <RadioGroup
            row
            value={form.fee_bearer}
            onChange={(e) => setForm({ ...form, fee_bearer: e.target.value })}
          >
            <FormControlLabel value="tazty" control={<Radio size="small" />} label="Tazty" />
            <FormControlLabel value="customer" control={<Radio size="small" />} label="Customer" />
          </RadioGroup>
        </Box>
        <TextField
          size="small"
          label="Fee percent"
          value={form.fee_percent}
          onChange={(e) => setForm({ ...form, fee_percent: e.target.value.replace(/[^\d.]/g, "") })}
          InputProps={{ endAdornment: <InputAdornment position="end">%</InputAdornment> }}
          sx={{ maxWidth: 160 }}
          helperText="Razorpay is about 2.36% incl. GST"
        />
        <Alert severity="info" sx={{ flex: 1, py: 0 }}>
          Example: customer adds ₹{example} →{" "}
          {form.fee_bearer === "customer"
            ? `pays ₹${example + exampleFee} (₹${exampleFee} convenience fee), wallet gets ₹${example}.`
            : `pays ₹${example}, wallet gets ₹${example}. Tazty pays the Razorpay fee.`}
        </Alert>
      </Stack>

      <Stack direction={{ xs: "column", md: "row" }} spacing={2} sx={{ mb: 2 }}>
        {money("min", "Minimum per top-up")}
        {money("max", "Maximum per top-up")}
        {money("balance_cap", "Added-money balance cap")}
        {money("daily_cap", "Daily limit per customer")}
      </Stack>

      {problem && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          {problem}
        </Alert>
      )}
      {form.enabled && (
        <Alert severity="warning" sx={{ mb: 1 }}>
          Customers can add money only after your CA has confirmed the closed-wallet (RBI) and
          accounting treatment.
        </Alert>
      )}
      <Button variant="contained" onClick={save} disabled={!!problem || saving}>
        {saving ? "Saving…" : "Save"}
      </Button>
    </Paper>
  );
};

export default TopupSettingsCard;
